import { useIsDesktopDictationFlagEnabled } from '@/app-shell/flags/dictate-flags';
import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { isTauri } from '@/tauri/utils';
import {
  useIsDictateDeviceDetectedPopupEnabled,
  useIsDictateDeviceSetupEnabled,
} from '@heidi/dictate/flags';
import { useUser } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

type ModalLaunchCoreStateSyncProps = {
  /**
   * Running-timer signal (`timerModel.isRunning`), passed in by the store-
   * coupled `_app` host per the Rematch containment ratchet — same shape as
   * `useIsClinicalRecordingActive`.
   */
  isTimerRunning: boolean;
};

/**
 * Keep the launch-modal queue's core app state alive on the on-prem build,
 * where the cloud-only writer is unmounted. `appStateForModalsAtom` was
 * written only by `global-modals-cloud-only.tsx`, so on-prem's
 * `isLaunchQueueBlocked` never lifted and a Universal-mounted queue surface
 * was cosmetic. This MERGES (never replaces) the core fields; the mount site
 * keeps this off SaaS builds, where the cloud-only writer already covers the
 * same fields and a second set of observers and writes would be duplication.
 */
export const ModalLaunchCoreStateSync = ({
  isTimerRunning,
}: ModalLaunchCoreStateSyncProps) => {
  const setAppStateForModals = useSetAtom(appStateForModalsAtom);
  const router = useRouter();
  const token = useAuthToken();
  const { data: user, isPending: isUserPending, isSuccess: isUserDataLoaded } = useUser();
  const { isPending: isKindePropertiesPending } = useQueryKindeProperties();
  const isClinicalRecordingActive = useIsClinicalRecordingActive(isTimerRunning);
  // Both dictation gates reach the launch row through the atom on every
  // build: this writer on on-prem, the cloud-only writer on SaaS.
  const isDictateDeviceDetectedPopupFlagEnabled =
    useIsDictateDeviceDetectedPopupEnabled();
  const isDictateDeviceSetupFlagEnabled = useIsDictateDeviceSetupEnabled();
  const isDesktopDictationFlagEnabled = useIsDesktopDictationFlagEnabled();

  useEffect(() => {
    setAppStateForModals((prev) => ({
      ...prev,
      hasToken: !!token,
      isUserPending,
      isUserDataLoaded,
      isKindePropertiesPending,
      pathname: router.pathname,
      isRecording: isTimerRunning,
      isClinicalRecordingActive,
      isUserManaged: !!user?.is_managed,
      isManaged: !!user?.is_managed,
      isTauri: isTauri(),
      isDictateDeviceDetectedPopupFlagEnabled,
      isDictateDeviceSetupFlagEnabled,
      isDesktopDictationFlagEnabled,
    }));
  }, [
    setAppStateForModals,
    token,
    isUserPending,
    isUserDataLoaded,
    isKindePropertiesPending,
    router.pathname,
    isTimerRunning,
    isClinicalRecordingActive,
    user?.is_managed,
    isDictateDeviceDetectedPopupFlagEnabled,
    isDictateDeviceSetupFlagEnabled,
    isDesktopDictationFlagEnabled,
  ]);

  return null;
};
